import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { TERRAIN_LINE_KIND, type TerrainLineKind } from '@visualizer/types';

// =====================================
// TYPES
// =====================================

export type GroundRoadLine = {
    line: LineSegments2;
    lineHalfWidth: number;
    lineColor?: number;
    lineKind: TerrainLineKind;
};

export type GroundRoadTexturesDescriptor = {
    texture: THREE.DataTexture;
    lineWidthsTexture: THREE.DataTexture;
    lineColorsTexture: THREE.DataTexture;
    lineKindsTexture: THREE.DataTexture;
    cellTexture: THREE.DataTexture;
    gridSize: number;
    worldMin: THREE.Vector2;
    worldSize: THREE.Vector2;
    maxSegmentsPerCell: number;
    defaultHalfWidth: number;
};

// Builds and stores GPU-friendly textures containing roads/borders projected into ground space.
// 1st texture contains line segment positions.
// 2nd texture contains line half-widths and optional colors.
// 3rd texture contains a grid-based index for fast shader culling of candidate segments.
export class GroundRoadTextures {
    // =====================================
    // CLASS FIELDS
    // =====================================

    static readonly DEFAULT_HALF_WIDTH = 0.5;
    static readonly POLYGON_HALF_WIDTH = 0.15;
    static readonly ROAD_HALF_WIDTH = 0.9;

    private lines: GroundRoadLine[] = [];
    private descriptor: GroundRoadTexturesDescriptor | null = null;

    // =====================================
    // LIFECYCLE
    // =====================================

    // Adds a new line source and rebuilds road textures:
    addLine(
        line: LineSegments2,
        lineHalfWidth = GroundRoadTextures.DEFAULT_HALF_WIDTH,
        lineColor?: number,
        lineKind: TerrainLineKind = TERRAIN_LINE_KIND.ROAD
    ) {
        this.lines.push({ line, lineHalfWidth, lineColor, lineKind });
        this.rebuildDescriptor();
    }

    // Clears all line sources and disposes cached textures:
    clearLines() {
        this.lines = [];
        this.disposeDescriptor();
    }

    // Returns the currently prepared textures descriptor, if available:
    getDescriptor(): GroundRoadTexturesDescriptor | null {
        return this.descriptor;
    }

    // Rebuilds descriptor to reflect current line visibility and geometry:
    refreshDescriptor() {
        this.rebuildDescriptor();
    }

    // Releases all allocated resources for this textures instance:
    dispose() {
        this.clearLines();
    }

    // Recomputes all road textures from current line sources:
    private rebuildDescriptor() {
        this.disposeDescriptor();
        const data = this.createLineTexturesData();
        this.descriptor = data;
    }

    // Disposes active textures and resets the cached descriptor:
    private disposeDescriptor() {
        this.descriptor?.texture.dispose();
        this.descriptor?.lineWidthsTexture.dispose();
        this.descriptor?.lineColorsTexture.dispose();
        this.descriptor?.lineKindsTexture.dispose();
        this.descriptor?.cellTexture.dispose();
        this.descriptor = null;
    }

    // =========================================
    // MAIN LOGIC TO BUILD THE ROAD TEXTURES
    // =========================================

    // Builds all road textures from visible lines:
    private createLineTexturesData(): GroundRoadTexturesDescriptor | null {
        if (this.lines.length === 0) return null;

        const { segments, widths, colors, kinds } = this.createBorderSegmentsFloatArray(this.lines);
        if (segments.length === 0) return null;

        const { texture, segmentCount, width, height } = this.createLineSegmentsTexture(segments);
        if (segmentCount === 0) return null;

        const lineWidthsTexture = this.createLineWidthsTexture(widths, width, height);
        const lineColorsTexture = this.createLineColorsTexture(colors, width, height);
        const lineKindsTexture = this.createLineWidthsTexture(kinds, width, height);

        const { min: worldMin, max: worldMax } = this.calculateWorldBounds(this.lines);
        const gridSize = 64;
        const maxSegmentsPerCell = 64;
        const cellTexture = this.createGridSegmentMapping(segments, segmentCount, worldMin, worldMax, gridSize, maxSegmentsPerCell);

        return {
            texture,
            lineWidthsTexture,
            lineColorsTexture,
            lineKindsTexture,
            cellTexture,
            gridSize,
            worldMin,
            worldSize: new THREE.Vector2().subVectors(worldMax, worldMin),
            maxSegmentsPerCell,
            defaultHalfWidth: GroundRoadTextures.DEFAULT_HALF_WIDTH,
        };
    }

    // Collects projected line segments plus width/color attributes from input line meshes:
    private createBorderSegmentsFloatArray(lines: GroundRoadLine[]): {
        segments: Float32Array;
        widths: Float32Array;
        colors: Float32Array;
        kinds: Float32Array;
    } {
        const segments: number[] = [];
        const widths: number[] = [];
        const colors: number[] = [];
        const kinds: number[] = [];
        const tempA = new THREE.Vector3();
        const tempB = new THREE.Vector3();
        const tempColorA = new THREE.Color();
        const tempColorB = new THREE.Color();

        for (const { line, lineHalfWidth, lineColor, lineKind } of lines) {
            const geometry = line.geometry as any;
            const startAttr = geometry.getAttribute('instanceStart');
            const endAttr = geometry.getAttribute('instanceEnd');
            const colorStartAttr = geometry.getAttribute('instanceColorStart');
            const colorEndAttr = geometry.getAttribute('instanceColorEnd');

            if (!startAttr || !endAttr || startAttr.count !== endAttr.count) {
                continue;
            }

            const hasSegmentColors =
                colorStartAttr && colorEndAttr && colorStartAttr.count === startAttr.count && colorEndAttr.count === endAttr.count;

            const fallbackColor = lineColor !== undefined ? new THREE.Color(lineColor) : null;

            for (let i = 0; i < startAttr.count; i++) {
                tempA.fromBufferAttribute(startAttr, i);
                tempB.fromBufferAttribute(endAttr, i);
                segments.push(tempA.x, -tempA.y, tempB.x, -tempB.y);
                widths.push(lineHalfWidth);
                kinds.push(lineKind);

                if (hasSegmentColors) {
                    tempColorA.fromBufferAttribute(colorStartAttr, i);
                    tempColorB.fromBufferAttribute(colorEndAttr, i);
                    tempColorA.lerp(tempColorB, 0.5);
                    colors.push(tempColorA.r, tempColorA.g, tempColorA.b, 1);
                } else if (fallbackColor) {
                    colors.push(fallbackColor.r, fallbackColor.g, fallbackColor.b, 1);
                } else {
                    colors.push(0, 0, 0, 0);
                }
            }
        }

        return {
            segments: new Float32Array(segments),
            widths: new Float32Array(widths),
            colors: new Float32Array(colors),
            kinds: new Float32Array(kinds),
        };
    }

    // =====================================
    // TEXTURES BUILDING
    // =====================================

    // 1st Texture: Packs 2D segment endpoints into a float texture.
    private createLineSegmentsTexture(data: Float32Array): {
        texture: THREE.DataTexture;
        segmentCount: number;
        width: number;
        height: number;
    } {
        const segmentCount = data.length / 4;
        const width = Math.ceil(Math.sqrt(segmentCount));
        const height = Math.ceil(segmentCount / width);

        const paddedLength = width * height * 4;
        const paddedData = new Float32Array(paddedLength);
        paddedData.set(data);

        const texture = new THREE.DataTexture(paddedData, width, height, THREE.RGBAFormat, THREE.FloatType);
        texture.needsUpdate = true;
        texture.magFilter = THREE.NearestFilter;
        texture.minFilter = THREE.NearestFilter;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;

        return { texture, segmentCount, width, height };
    }

    // 2nd Texture: Packs per-segment half-width values into an R32F texture.
    private createLineWidthsTexture(data: Float32Array, width: number, height: number): THREE.DataTexture {
        const paddedLength = width * height;
        const paddedData = new Float32Array(paddedLength);
        paddedData.set(data);

        const texture = new THREE.DataTexture(paddedData, width, height, THREE.RedFormat, THREE.FloatType);
        (texture as any).internalFormat = 'R32F';
        texture.needsUpdate = true;
        texture.magFilter = THREE.NearestFilter;
        texture.minFilter = THREE.NearestFilter;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;

        return texture;
    }

    // 2nd Texture (companion): Packs per-segment color values into an RGBA float texture.
    private createLineColorsTexture(data: Float32Array, width: number, height: number): THREE.DataTexture {
        const paddedLength = width * height * 4;
        const paddedData = new Float32Array(paddedLength);
        paddedData.set(data);

        const texture = new THREE.DataTexture(paddedData, width, height, THREE.RGBAFormat, THREE.FloatType);
        texture.needsUpdate = true;
        texture.magFilter = THREE.NearestFilter;
        texture.minFilter = THREE.NearestFilter;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;

        return texture;
    }

    // 3rd Texture: Builds a grid-cell to segment-index lookup texture for fast shader computations.
    private createGridSegmentMapping(
        segments: Float32Array,
        segmentCount: number,
        worldMin: THREE.Vector2,
        worldMax: THREE.Vector2,
        gridSize: number,
        maxSegmentsPerCell: number
    ): THREE.DataTexture {
        // 1) Allocate a fixed-size (64x64) grid where each cell stores candidate segment indices.
        const cellCount = gridSize * gridSize;
        const cellSegmentIndices = new Float32Array(cellCount * maxSegmentsPerCell).fill(-1);
        const worldSize = new THREE.Vector2().subVectors(worldMax, worldMin);

        // 2) For each segment, compute its world-space AABB and map it to grid-cell bounds.
        for (let i = 0; i < segmentCount; i++) {
            const x1 = segments[i * 4];
            const z1 = segments[i * 4 + 1];
            const x2 = segments[i * 4 + 2];
            const z2 = segments[i * 4 + 3];

            const minX = Math.min(x1, x2);
            const maxX = Math.max(x1, x2);
            const minZ = Math.min(z1, z2);
            const maxZ = Math.max(z1, z2);

            const minCellX = Math.floor(((minX - worldMin.x) / worldSize.x) * gridSize);
            const maxCellX = Math.floor(((maxX - worldMin.x) / worldSize.x) * gridSize);
            const minCellZ = Math.floor(((minZ - worldMin.y) / worldSize.y) * gridSize);
            const maxCellZ = Math.floor(((maxZ - worldMin.y) / worldSize.y) * gridSize);

            // 2.1.) Iterate through every grid cell overlapped by the segment AABB.
            for (let gx = minCellX; gx <= maxCellX; gx++) {
                for (let gz = minCellZ; gz <= maxCellZ; gz++) {
                    if (gx < 0 || gx >= gridSize || gz < 0 || gz >= gridSize) continue;
                    const cellIndex = gz * gridSize + gx;
                    const base = cellIndex * maxSegmentsPerCell;
                    // 2.2.) Insert the segment index into the first free slot for that cell.
                    for (let s = 0; s < maxSegmentsPerCell; s++) {
                        if (cellSegmentIndices[base + s] === -1) {
                            cellSegmentIndices[base + s] = i;
                            break;
                        }
                    }
                }
            }
        }

        // 3) Upload the lookup buffer as an R32F texture for shader-side candidate filtering.
        const tex = new THREE.DataTexture(cellSegmentIndices, maxSegmentsPerCell, gridSize * gridSize, THREE.RedFormat, THREE.FloatType);
        (tex as any).internalFormat = 'R32F';
        tex.needsUpdate = true;
        tex.magFilter = THREE.NearestFilter;
        tex.minFilter = THREE.NearestFilter;
        tex.wrapS = THREE.ClampToEdgeWrapping;
        tex.wrapT = THREE.ClampToEdgeWrapping;

        return tex;
    }

    // Computes projected 2D world bounds for all visible input lines.
    private calculateWorldBounds(lines: GroundRoadLine[]): { min: THREE.Vector2; max: THREE.Vector2 } {
        let minX = Infinity;
        let maxX = -Infinity;
        let minZ = Infinity;
        let maxZ = -Infinity;

        const tempA = new THREE.Vector3();
        const tempB = new THREE.Vector3();

        for (const { line } of lines) {
            const geometry = line.geometry as any;
            const startAttr = geometry.getAttribute('instanceStart');
            const endAttr = geometry.getAttribute('instanceEnd');

            if (!startAttr || !endAttr || startAttr.count !== endAttr.count) {
                continue;
            }

            for (let i = 0; i < startAttr.count; i++) {
                tempA.fromBufferAttribute(startAttr, i);
                tempB.fromBufferAttribute(endAttr, i);

                minX = Math.min(minX, tempA.x, tempB.x);
                maxX = Math.max(maxX, tempA.x, tempB.x);
                minZ = Math.min(minZ, -tempA.y, -tempB.y);
                maxZ = Math.max(maxZ, -tempA.y, -tempB.y);
            }
        }

        const padding = 1.0;
        return {
            min: new THREE.Vector2(minX - padding, minZ - padding),
            max: new THREE.Vector2(maxX + padding, maxZ + padding),
        };
    }
}
